import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Handshake, Shield, ArrowLeft, CreditCard, Wallet, Receipt, Send } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import AdminHeader from "../components/admin/AdminHeader";

const COLORS = {
  bg: "#F4F4F0",
  bgAlt: "#ede5d6",
  ink: "#1a2330",
  inkSoft: "#4a5568",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  card: "#ffffff",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
};
const SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const API_BASE = process.env.REACT_APP_API_URL || "";

// Stripe PaymentIntent / payout statuses → a colored pill.
const STATUS_STYLES = {
  succeeded:                { bg: "#e8efea", fg: "#1f6f43" },
  paid:                     { bg: "#e8efea", fg: "#1f6f43" },
  requires_capture:         { bg: "#fff3d6", fg: "#8a5a00" },
  processing:               { bg: "#fff3d6", fg: "#8a5a00" },
  pending:                  { bg: "#fff3d6", fg: "#8a5a00" },
  requires_payment_method:  { bg: "#ececec", fg: "#555555" },
  requires_confirmation:    { bg: "#ececec", fg: "#555555" },
  canceled:                 { bg: "#f3e3e3", fg: "#8a2a2a" },
  refunded:                 { bg: "#e3edf7", fg: "#1f4a7a" },
  failed:                   { bg: "#f3e3e3", fg: "#8a2a2a" },
};
const KIND_LABELS = {
  fee: "Filing fee",
  filer_stake: "Filer stake",
  disputant_stake: "Disputant stake",
};

function money(cents, currency = "usd") {
  if (cents == null) return "—";
  return `${(cents / 100).toLocaleString(undefined, { style: "currency", currency: currency.toUpperCase() })}`;
}
function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { bg: COLORS.bgAlt, fg: COLORS.inkSoft };
  return (
    <span style={{ ...styles.statusBadge, background: s.bg, color: s.fg }}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

export default function AdminUserStripe() {
  const { userId } = useParams();
  const navigate = useNavigate();

  // Authorization header for /api/ai/admin/* — either a Basic Auth session
  // (sessionStorage-backed) or auto-detected via the current user's JWT if
  // they're an is_admin account. See useAdminAuth.
  const {
    authHeader,
    resolving: resolvingAuth,
    loginUser,
    setLoginUser,
    loginPass,
    setLoginPass,
    loginError,
    handleLogin,
    handleAdminLogout,
    handleUnauthorized,
  } = useAdminAuth(`/api/ai/admin/users/${userId}/stripe`);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (header) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/users/${userId}/stripe`, {
        headers: { Authorization: header },
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, handleUnauthorized]);

  useEffect(() => {
    if (authHeader) load(authHeader);
  }, [authHeader, load]);

  // While a logged-in user's JWT might still resolve to an is_admin session,
  // hold off on the login form so it doesn't flash before auto-login lands.
  if (resolvingAuth) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <p style={styles.loginSubtitle}>Checking your session…</p>
        </div>
      </div>
    );
  }

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!authHeader) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <div style={styles.adminPill}>
            <Shield size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
            Admin sign-in
          </div>
          <h1 style={styles.loginTitle}>Restricted area</h1>
          <p style={styles.loginSubtitle}>
            This panel uses its own credentials. They are independent from your normal account.
          </p>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <input type="text" autoComplete="username" placeholder="Admin username"
              value={loginUser} onChange={(e) => setLoginUser(e.target.value)} style={styles.loginInput} autoFocus />
            <input type="password" autoComplete="current-password" placeholder="Admin password"
              value={loginPass} onChange={(e) => setLoginPass(e.target.value)} style={styles.loginInput} />
            {loginError && <div style={styles.loginErr}>{loginError}</div>}
            <button type="submit" style={styles.loginBtn}>Enter admin panel</button>
            <button type="button" onClick={() => navigate("/admin/users")} style={styles.loginGhost}>Back to users</button>
          </form>
        </div>
      </div>
    );
  }

  const u = data?.user;
  const cards = data?.cards || [];
  const payments = data?.payments || [];
  const payouts = data?.payouts || [];

  // ── Authenticated admin view ───────────────────────────────────────────────
  return (
    <div style={styles.root}>
      <AdminHeader onLogout={handleAdminLogout} showNewDispute={false} />

      <main style={styles.main}>
        <button type="button" onClick={() => navigate("/admin/users")} style={styles.backLink}>
          <ArrowLeft size={14} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
          Back to users
        </button>

        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.title}>Stripe data</h1>
            <p style={styles.subtitle}>
              {loading
                ? "Loading…"
                : u
                ? <>{u.username} · <span style={styles.subtitleMono}>{u.email}</span> · user #{u.id}</>
                : `User #${userId}`}
            </p>
          </div>
        </div>

        {error && <div style={styles.errorBox}>Failed to load: {error}</div>}

        {!loading && !error && u && (
          <>
            {/* ── Stripe account identifiers ── */}
            <div style={styles.idGrid}>
              <div style={styles.idCard}>
                <div style={styles.idLabel}><CreditCard size={13} strokeWidth={2} style={{ marginRight: 6 }} />Customer (saved cards)</div>
                {u.stripe_customer_id
                  ? <code style={styles.idValue}>{u.stripe_customer_id}</code>
                  : <span style={styles.idEmpty}>None — no Stripe customer created</span>}
              </div>
              <div style={styles.idCard}>
                <div style={styles.idLabel}><Wallet size={13} strokeWidth={2} style={{ marginRight: 6 }} />Connect account (payouts)</div>
                {u.stripe_connect_account_id
                  ? <code style={styles.idValue}>{u.stripe_connect_account_id}</code>
                  : <span style={styles.idEmpty}>None — not onboarded for payouts</span>}
              </div>
            </div>

            {/* ── Saved cards ── */}
            <section style={styles.section}>
              <h2 style={styles.sectionTitle}><CreditCard size={16} strokeWidth={2} style={{ marginRight: 8 }} />Saved cards ({cards.length})</h2>
              {cards.length === 0 ? (
                <div style={styles.emptyRow}>No cards cached in our DB. (Stripe remains the source of truth.)</div>
              ) : (
                <div style={styles.cardsWrap}>
                  {cards.map((c) => (
                    <div key={c.stripe_payment_method_id} style={styles.cardChip}>
                      <span style={styles.cardBrand}>{c.brand || "card"}</span>
                      <span style={styles.cardLast4}>•••• {c.last4 || "????"}</span>
                      <span style={styles.cardExp}>{c.exp_month || "--"}/{c.exp_year || "----"}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ── Payments ── */}
            <section style={styles.section}>
              <h2 style={styles.sectionTitle}><Receipt size={16} strokeWidth={2} style={{ marginRight: 8 }} />Payments ({payments.length})</h2>
              {payments.length === 0 ? (
                <div style={styles.emptyRow}>No PaymentIntents recorded for this user.</div>
              ) : (
                <div style={styles.table} role="table">
                  <div style={{ ...styles.tableHeader, ...styles.paymentGrid }} role="row">
                    <div>Kind</div><div>Amount</div><div>Status</div><div>Dispute</div><div>PaymentIntent</div><div>Created</div>
                  </div>
                  {payments.map((p) => (
                    <div key={p.id} style={{ ...styles.tableRow, ...styles.paymentGrid }} role="row">
                      <div><span style={styles.kindTag}>{KIND_LABELS[p.kind] || p.kind}</span></div>
                      <div style={styles.amount}>{money(p.amount, p.currency)}</div>
                      <div><StatusPill status={p.status} /></div>
                      <div style={styles.ellipsis}>
                        {p.dispute_uuid ? (
                          <button type="button" onClick={() => navigate(`/dispute/${p.dispute_uuid}`)} style={styles.disputeLink} title={p.dispute_subject || ""}>
                            {p.dispute_subject || p.dispute_uuid}
                          </button>
                        ) : "—"}
                      </div>
                      <div><code style={styles.mono} title={p.stripe_payment_intent_id || ""}>{p.stripe_payment_intent_id || "—"}</code></div>
                      <div style={styles.dateCell}>{formatDate(p.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ── Payouts ── */}
            <section style={styles.section}>
              <h2 style={styles.sectionTitle}><Send size={16} strokeWidth={2} style={{ marginRight: 8 }} />Payouts ({payouts.length})</h2>
              {payouts.length === 0 ? (
                <div style={styles.emptyRow}>No Connect transfers recorded for this user.</div>
              ) : (
                <div style={styles.table} role="table">
                  <div style={{ ...styles.tableHeader, ...styles.payoutGrid }} role="row">
                    <div>Amount</div><div>Status</div><div>Dispute</div><div>Transfer</div><div>Created</div>
                  </div>
                  {payouts.map((p) => (
                    <div key={p.id} style={{ ...styles.tableRow, ...styles.payoutGrid }} role="row">
                      <div style={styles.amount}>{money(p.amount, p.currency)}</div>
                      <div><StatusPill status={p.status} /></div>
                      <div style={styles.ellipsis}>
                        {p.dispute_uuid ? (
                          <button type="button" onClick={() => navigate(`/dispute/${p.dispute_uuid}`)} style={styles.disputeLink} title={p.dispute_subject || ""}>
                            {p.dispute_subject || p.dispute_uuid}
                          </button>
                        ) : "—"}
                      </div>
                      <div><code style={styles.mono} title={p.stripe_transfer_id || ""}>{p.stripe_transfer_id || "—"}</code></div>
                      <div style={styles.dateCell}>{formatDate(p.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, fontSize: 14 },

  // ── Login screen ─────────────────────────────────────────────────────────
  loginRoot: {
    minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
  },
  loginCard: {
    width: "100%", maxWidth: 420, background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
    padding: "32px 28px", boxShadow: "0 10px 30px rgba(15, 30, 45, 0.08)",
  },
  loginBrand: { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.ink, marginBottom: 16 },
  adminPill: {
    display: "inline-block", background: COLORS.dark, color: "#fff",
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
  },
  loginTitle: { fontFamily: SERIF, fontSize: 26, fontWeight: 600, margin: "0 0 8px 0", letterSpacing: "-0.3px", color: COLORS.ink },
  loginSubtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "0 0 20px 0", lineHeight: 1.5 },
  loginForm: { display: "flex", flexDirection: "column", gap: 12 },
  loginInput: {
    width: "100%", boxSizing: "border-box", background: COLORS.bg, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: SANS, outline: "none",
  },
  loginErr: { background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 6, padding: "8px 10px", fontSize: 12 },
  loginBtn: { background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8, padding: "11px 16px", fontSize: 14, fontWeight: 600, fontFamily: SANS, cursor: "pointer" },
  loginGhost: { background: "transparent", color: COLORS.inkSoft, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "9px 16px", fontSize: 13, fontFamily: SANS, cursor: "pointer" },

  // ── Authenticated layout ─────────────────────────────────────────────────
  logo: { fontFamily: SERIF, fontSize: 22, fontWeight: 600, letterSpacing: "-0.3px" },
  logoItalic: { fontStyle: "italic", fontWeight: 600 },

  main: { padding: "24px clamp(16px, 4vw, 48px) 48px", maxWidth: 1280, margin: "0 auto" },
  backLink: { background: "transparent", border: "none", color: COLORS.inkSoft, fontSize: 13, fontFamily: SANS, cursor: "pointer", padding: 0, marginBottom: 12 },

  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 24 },
  title: { fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, letterSpacing: "-0.5px" },
  subtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "6px 0 0 0" },
  subtitleMono: { fontFamily: MONO, fontSize: 12 },

  errorBox: { padding: 16, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 8, marginBottom: 16 },

  idGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 24 },
  idCard: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "16px 20px" },
  idLabel: { display: "flex", alignItems: "center", fontSize: 11, fontWeight: 700, color: COLORS.inkMuted, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10 },
  idValue: { fontFamily: MONO, fontSize: 13, color: COLORS.ink, wordBreak: "break-all" },
  idEmpty: { fontSize: 13, color: COLORS.inkMuted, fontStyle: "italic" },

  section: { marginBottom: 28 },
  sectionTitle: { display: "flex", alignItems: "center", fontFamily: SERIF, fontSize: 20, fontWeight: 600, margin: "0 0 14px 0", letterSpacing: "-0.2px" },
  emptyRow: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "20px 24px", fontSize: 13, color: COLORS.inkMuted },

  cardsWrap: { display: "flex", flexWrap: "wrap", gap: 10 },
  cardChip: {
    display: "inline-flex", alignItems: "center", gap: 10,
    background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 14px",
  },
  cardBrand: { fontSize: 12, fontWeight: 700, textTransform: "capitalize", color: COLORS.ink },
  cardLast4: { fontFamily: MONO, fontSize: 13, color: COLORS.inkSoft },
  cardExp: { fontFamily: MONO, fontSize: 12, color: COLORS.inkMuted },

  table: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden" },
  tableHeader: {
    display: "grid", gap: 16, padding: "12px 20px", background: COLORS.bgAlt, color: COLORS.inkMuted,
    fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, borderBottom: `1px solid ${COLORS.border}`,
  },
  tableRow: { display: "grid", gap: 16, padding: "14px 20px", alignItems: "center", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 },
  paymentGrid: { gridTemplateColumns: "minmax(0,1.1fr) 0.9fr 1fr minmax(0,1.4fr) minmax(0,1.6fr) 1.1fr" },
  payoutGrid: { gridTemplateColumns: "0.9fr 1fr minmax(0,1.6fr) minmax(0,1.8fr) 1.1fr" },

  kindTag: { display: "inline-block", fontSize: 11, fontWeight: 600, color: COLORS.inkSoft, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" },
  amount: { fontFamily: MONO, fontSize: 13, fontWeight: 600, color: COLORS.ink },
  statusBadge: { display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, textTransform: "capitalize", whiteSpace: "nowrap" },
  ellipsis: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  disputeLink: { background: "transparent", border: "none", padding: 0, color: COLORS.ink, fontFamily: SANS, fontSize: 13, fontWeight: 600, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" },
  mono: { fontFamily: MONO, fontSize: 11, color: COLORS.inkSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" },
  dateCell: { fontFamily: MONO, fontSize: 11, color: COLORS.inkMuted },
};
